import * as React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Stack,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import LaunchIcon from "@mui/icons-material/Launch";
import DateRangeIcon from "@mui/icons-material/DateRange";
import navy from "../../images/navy_logo.png";
import quevera from "../../images/quevera_logo.jpeg";
import codePlatoon from "../../images/code_platoon_logo.png";
import fullStackLabs from "../../images/fullstack_labs_logo.png";
import resupply from "../../images/resupplyme_logo.jpeg";

const experience = [
  {
    name: "Quevera",
    date: "Oct 2022 - March 2026",
    role: "Full-Stack Software Engineer",
    location: "Columbia, MD (Remote)",
    website: "https://quevera.com/",
    logo: quevera,
    skills: [
      "React",
      "Redux Toolkit",
      "TypeScript",
      "TanStack Table",
      "MUI",
      "Django",
      "Django REST Framework",
      "PostgreSQL",
      "Amazon Bedrock",
      "RAG Architectures",
      "AWS Glue (PySpark)",
      "Athena",
      "QuickSight",
      "S3",
      "Amazon Textract",
      "OpenSearch",
      "Cypress",
      "React Testing Library",
      "Mock Service Worker",
      "GitLab CI/CD",
      "Mentoring",
    ],
    about:
      "Served as both sole engineer and technical lead across multiple projects, owning the frontend, backend/API, AI/ML, data pipeline, and testing workstreams end-to-end. Architected and delivered an AI chatbot using Amazon Bedrock with a full RAG pipeline over S3, enabling natural language queries across large volumes of business documentation. Built a real-time analytics platform with AWS Glue (PySpark), partitioned S3/Parquet, Athena, and QuickSight. Led frontend development with React, Redux Toolkit, TypeScript, and TanStack Table, and designed secure RESTful APIs using Django REST Framework. Drove test coverage from baseline to 90%+ using RTL, Cypress, and MSW integrated into GitLab CI/CD. Participated in a full legacy modernization effort, migrating a ColdFusion application and SQL Server data onto AWS-native infrastructure. Mentored junior engineers through code reviews, pairing, and onboarding, and established team-wide coding standards.",
  },
  {
    name: "Code Platoon's Immersive, Full-Stack Software Engineering Program",
    date: "May 2022 - September 2022",
    role: "Graduate",
    location: "Remote (Chicago, IL)",
    website: "https://www.codeplatoon.org/",
    logo: codePlatoon,
    skills: [
      "JavaScript",
      "Python",
      "React",
      "Django",
      "REST",
      "HTML/CSS",
      "Git/Github",
      "OOP",
      "Pair Programming",
    ],
    about:
      "As part of a 15-week course, dedicated over 1,000 hours learning how to build full-stack web applications. Utilized Github and pair programming to collaborate with team members while following an Agile workflow.",
  },
  {
    name: "FullStack Labs",
    date: "Oct 2021 - Dec 2021",
    role: "Quality Assurance Professional",
    location: "San Francisco, CA",
    website: "https://www.fullstacklabs.co/",
    logo: fullStackLabs,
    skills: [
      "Manual Testing",
      "Android Studio",
      "Xcode",
      "Device Emulators",
      "Jira",
      "Bug Reports",
      "Cross-platform Testing",
      "Agile",
    ],
    about:
      "Executed comprehensive manual test cases across Windows, macOS, and Linux using structured QA protocols, ensuring software reliability prior to deployment in mission-critical environments. Led mobile testing across Android and iOS using Android Studio, Xcode, and device emulators, identifying and documenting cross-platform compatibility issues. Authored clear, reproducible test cases, detailed bug reports, and user stories in Jira, improving transparency between QA, development, and product teams. Collaborated closely with developers to triage and reproduce defects, significantly reducing back-and-forth and shortening fix cycles.",
  },
  {
    name: "ReSupply",
    date: "Feb 2021 - July 2021",
    role: "Intern",
    location: "Remote (Boston, MA)",
    website: "https://resupplyapp.com/",
    logo: resupply,
    skills: ["Scrum", "Project Life Cycle", "ClickUp", "Retool"],
    about:
      "Gained significant start-up experience interning at ReSupply. Worked under the PM learning Project Life Cycle and Scrum. Worked as Quality Assurance, manually testing and creating tickets via PMS, ClickUp",
  },
  {
    name: "United States Navy",
    date: "July 2017 - July 2021",
    role: "Information Technology Specialist (IT)",
    location: "Norfolk, VA",
    website: "https://www.navy.com/",
    logo: navy,
    skills: [
      "Satellite Communications",
      "Cryptologic Systems",
      "Network Operations",
      "Technical Training",
      "Preventative Maintenance",
      "Leadership",
      "Effective Communication",
      "Adaptability",
    ],
    about:
      "Managed and optimized secure satellite communications systems supporting critical operational missions, conducting real-time monitoring and preventative maintenance to ensure high network availability across global deployments. Led and delivered over 1,000 hours of technical training to a team of 20+ personnel on cryptologic systems, secure satellite networks, and mission-critical communication protocols, developing tailored instruction plans that enhanced team readiness. Demonstrated consistent leadership under pressure, maintaining 24/7 system uptime and enabling uninterrupted communications during joint operations, naval exercises, and real-world tasking.",
  },
];

export default function ExperienceCards() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {experience.map((job, index) => (
          <Grid item xs={12} key={index}>
            <Card
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <Box
                      component="img"
                      src={job.logo}
                      alt={`${job.name} logo`}
                      sx={{
                        width: '100%',
                        maxWidth: 100,
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: 1,
                        bgcolor: 'white',
                        p: 1
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={10}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="h3" sx={{ color: '#72efdd', mb: 0.5 }}>
                          {job.role}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: 'white' }}>
                          {job.name}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <DateRangeIcon sx={{ color: '#7E4DE2', fontSize: '1.2rem' }} />
                        {job.date}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WorkIcon sx={{ color: '#7E4DE2', mr: 1 }} />
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {job.location}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LaunchIcon sx={{ color: '#7E4DE2', mr: 1 }} />
                      <Typography 
                        component="a" 
                        href={job.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'white',
                          textDecoration: 'none',
                          '&:hover': {
                            color: '#72efdd'
                          }
                        }}
                      >
                        {job.website}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ color: 'white', mb: 2 }}>
                      {job.about}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ 
                        flexWrap: 'wrap',
                        gap: 1
                      }}
                    >
                      {job.skills.map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(114, 239, 221, 0.1)',
                            color: '#72efdd',
                            border: '1px solid rgba(114, 239, 221, 0.2)',
                            '&:hover': {
                              bgcolor: 'rgba(114, 239, 221, 0.2)',
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
